import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import WorldLabel, { nameProps } from './WorldLabel';
import { useWorldStore } from '../state/worldStore';
import AnimatedResident from './AnimatedResident';
import { maybeBark } from '../services/barkEngine';
import { ktx2Extend } from '../utils/ktx2';
import { STREET_SPAWN } from '../streetSpawn';
import { IS_MOBILE } from '../config';

// Npc — one resident in the city-life system (Spec: two-tier NPC system).
// Wraps the rigged body with the tier plumbing: proximity drives far → mid →
// near → engaged, a floating label reflects it, and tapping an engageable NPC
// hands off to the live voice chat (Tier E).
//
// Placement editor (?place=edit): click a resident to select, arrows move it on
// X/Z, [ ] rotate; the tuned post persists per-slug in localStorage and logs a
// paste-ready line for roster.json (NpcPlacePanel is the on-screen control).

export const labelStyle = (hot) => ({
  padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap',
  font: '600 12px system-ui, sans-serif',
  color: hot ? '#1c1206' : '#ffe9c4',
  background: hot ? '#ffb25e' : 'rgba(16,10,6,0.72)',
  border: '1px solid rgba(255,178,94,0.4)',
  backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
  transform: 'translateY(-6px)', pointerEvents: 'none', userSelect: 'none',
});

// Deterministic per-NPC hash → a stable idle phase offset + playback rate, so a
// crowd of the same idle clip doesn't move in lockstep (and drifts over time).
const hash = (n) => { const x = Math.sin(n * 12.9898) * 43758.5453; return x - Math.floor(x); };
// Single source of truth = roster.json (autosaved). No localStorage — mixing
// the two made sizes depend on ?fresh and diverge between returns.
const loadPos = (npc) => ({ pos: npc.post, rotY: npc.rotationY || 0, height: npc.height || 1.8 });

// Streaming radii (metres). Wide gap on purpose — see the gate in Npc.
const qpNum = (k, d) => {
  const v = typeof window !== 'undefined' && parseFloat(new URLSearchParams(window.location.search).get(k));
  return Number.isFinite(v) ? v : d;
};
// PREFETCH is where the download starts; LOAD is where the body is built.
//
// These are two different costs and only one of them has to be near you.
// Fetching and decoding ~5.8MB happens in the loader, off the critical path —
// it can start from across the block and nobody notices. MOUNTING is what
// stalls a frame (skinning setup, texture upload), so that stays close.
//
// Collapsing them into one radius is what made residents take ten seconds:
// nothing began downloading until you were already 35m away and walking, so the
// whole transfer happened while you stood in front of an empty pavement.
// Starting at 110m means the bytes are usually there before you arrive, and the
// mount is all that is left to pay for.
// ⚠ A PHONE HAS A BUDGET (2026-10-29: entering the city crashed Safari). The
// ICT-face bodies are ~55 MB each on the GPU, and these radii had no phone
// values: everyone within 110 units was PARSED ahead (useGLTF.preload decodes
// textures, it is not just a download) and everyone within 55 mounted, all on
// arrival. On a phone: no parse-ahead, a short load radius, unload soon after,
// one body at a time, and at most BODY_CAP bodies at once (the rest stand in
// as silhouettes until a slot frees). Desktop keeps its values. ?loadr= etc.
// still override for testing.
export const PREFETCH_RADIUS = qpNum('prefetchr', IS_MOBILE ? 0 : 110);
export const LOAD_RADIUS = qpNum('loadr', IS_MOBILE ? 22 : 55);
export const UNLOAD_RADIUS = qpNum('unloadr', IS_MOBILE ? 32 : 150);
// Mounts may overlap now that the bytes usually land first — a mount off a warm
// cache is short enough that two at once is not felt (not on a phone).
const MAX_IN_FLIGHT = qpNum('loadq', IS_MOBILE ? 1 : 2);
const BODY_CAP = qpNum('bodycap', IS_MOBILE ? 4 : 0);   // 0 = no cap

// ONE BODY AT A TIME.
//
// A radius alone does not fix the load pause, because the residents are
// clustered: tightening it to where only two or three are in range makes them
// pop in at conversation distance, and even three bodies decoding in the same
// frame stalls the main thread. GLB parse, skinning setup and texture upload are
// all synchronous — the cost is not the download, it is landing them together.
//
// So admissions are queued. One body arrives, and only once it is standing does
// the next begin. NEAREST FIRST, so the resident you are walking toward is the
// one that resolves first rather than whoever happened to ask earliest. The
// street fills in over a couple of seconds and every frame stays interactive.
const waiting = new Map();          // slug -> { d, wake }
let inFlight = 0;
const mounted = new Set();          // slugs with a body up (or coming up): the phone's cap counts these

function pump() {
  while (inFlight < MAX_IN_FLIGHT && waiting.size) {
    if (BODY_CAP && mounted.size >= BODY_CAP) break;
    const [slug, entry] = [...waiting].sort((a, b) => a[1].d - b[1].d)[0];
    waiting.delete(slug);
    inFlight += 1;
    mounted.add(slug);
    entry.wake();
  }
}
/** A body came down (left range, or its resident left): its place under the cap frees. */
export function bodyGone(slug) {
  if (mounted.delete(slug)) pump();
}
// Exported: the Living City's walking residents (city/CityResident) take their
// turn in the SAME queue, so a street of walkers still builds one body at a time.
export function requestLoad(slug, d, wake) { waiting.set(slug, { d, wake }); pump(); }
// Called both when a body finishes arriving AND when one leaves range while
// still queued — a slot that is never released stalls the queue for everyone.
export function releaseLoad(slug, heldSlot) {
  waiting.delete(slug);
  if (heldSlot) inFlight = Math.max(0, inFlight - 1);
  pump();
}

// Placement no longer saves from here. It used to POST to /__npc-save, which is
// a VITE DEV-SERVER middleware: it does not exist in a built app, so on the
// deployed world every move was silently discarded, and even locally it wrote a
// file that needed a redeploy before anyone else saw it. That is why position,
// rotation and size never survived.
//
// Residents now come from world_room_objects (room_id 'street') merged over
// roster.json in NpcManager, and NpcPlacePanel owns the editing and the Save.
// This component just renders what it is given.

export default function Npc({ npc }) {
  const openChat = useWorldStore((s) => s.openChat);
  const setNpcEdit = useWorldStore((s) => s.setNpcEdit);
  const selectedSlug = useWorldStore((s) => s.npcEdit.slug);
  const PLACE = useWorldStore((s) => s.adminTool) === 'place'; // placement edit mode (AdminPanel)
  const [tier, setTier] = useState('far');
  // The merged roster entry is the ONLY source of the transform — NpcManager
  // lays saved world_room_objects rows over roster.json, so a placement made in
  // the panel arrives here as a prop change and the body moves live.
  //
  // Height is synced too. The old version deliberately stopped syncing while
  // placing and never carried height at all, which is why a saved size never
  // reached the body even when the number had been written down correctly.
  const xf = useMemo(() => loadPos(npc), [npc.post, npc.rotationY, npc.height]);
  const onTier = useCallback((t) => setTier(t), []);
  // Ambient barks (Tier A): the resident murmurs in their own voice when you
  // HOVER over them (deliberate, unlike a walk-by) — a greeting, or idle chatter
  // for variety. barkEngine cooldowns keep repeated hovers from spamming.
  const onHover = useCallback(() => {
    if (PLACE || npc.avatarId == null) return;
    maybeBark(npc.slug, Math.random() < 0.6 ? 'greeting_passerby' : 'idle_chatter');
  }, [npc.slug, npc.avatarId]);

  const seed = npc.avatarId ?? Array.from(npc.slug || 'x').reduce((a, c) => a + c.charCodeAt(0), 0);
  const phaseOffset = hash(seed + 1);
  const rate = 0.9 + hash(seed + 2) * 0.2; // 0.9–1.1×

  const engageable = npc.avatarId != null && !!npc.rigged;
  const selected = PLACE && selectedSlug === npc.slug;

  // Editing lives in NpcPlacePanel now, not on the keyboard. The street camera
  // already listens for arrows and WASD on window, so a placement handler bound
  // to the same keys moved the camera and the resident together.

  // Click behavior: select for placement in edit mode, else engage the chat.
  const onSelect = PLACE ? () => setNpcEdit({ slug: npc.slug }) : (engageable ? () => openChat({ avatarId: npc.avatarId, name: npc.name }) : null);

  // STREAMING GATE — do not pay for a body you cannot see.
  //
  // Every roster entry used to mount its rigged GLB the moment the street
  // loaded: twelve bodies, ~67MB, fetched and decoded together before the first
  // frame. That is the pause on load, and it is spent on residents who are a
  // hundred metres away and a few pixels tall.
  //
  // Two radii, not one. A single threshold thrashes — stand exactly on it and
  // the body mounts and unmounts every frame, which is far worse than either
  // state. Loading at 45m and releasing at 130m means crossing back and forth
  // costs nothing.
  //
  // Unmounting drops the render instance but deliberately LEAVES the drei GLTF
  // cache alone: clearing it would re-download 6MB every time you walk a block
  // and come back. The win being chased here is the cold-start pause, and the
  // cache is what makes the second approach instant.
  // KTX2 transcoder, matched to what AnimatedResident mounts with.
  const gl = useThree((st) => st.gl);
  const ktx2 = useMemo(() => ktx2Extend(gl), [gl]);
  const prefetchedRef = useRef(false);
  const [bodyOn, setBodyOn] = useState(false);
  const bodyOnRef = useRef(false);   // body is mounted
  const queuedRef = useRef(false);   // waiting for admission
  const slotRef = useRef(false);     // holds a queue slot (released once standing)
  useFrame(({ camera }) => {
    const dCam = Math.hypot(camera.position.x - xf.pos[0], camera.position.z - xf.pos[2]);
    // BEFORE ARRIVAL, THE MOUNT GATE ALSO MEASURES FROM WHERE THE PLAYER WILL
    // LAND (see streetSpawn.js) — so the residents around the landing spot load
    // behind the loading gate instead of popping in, and stalling, mid fly-in.
    //
    // ONLY THE MOUNT GATE, never the prefetch below. Mounts go through the queue
    // (MAX_IN_FLIGHT at a time); the prefetch is fire-and-forget, and letting it
    // use the spawn distance fired every nearby body's rigged + walk + idle at
    // once at boot — ~40 large GLBs through the dev proxy together, which is
    // enough to make the browser give up (ERR_TOO_MANY_RETRIES) on unrelated
    // requests and crash the Canvas on a failed car. Khari hit it 2026-09-23.
    const d = useWorldStore.getState().arrivedAt ? dCam
      : Math.min(dCam, Math.hypot(STREET_SPAWN.pos[0] - xf.pos[0], STREET_SPAWN.pos[1] - xf.pos[2]));

    // Warm the cache long before the body is needed. Fire-and-forget and
    // deliberately OUTSIDE the mount queue: a download is not a frame cost, so
    // making it wait its turn behind another resident's mount would reintroduce
    // exactly the delay this exists to remove.
    if (!prefetchedRef.current && dCam < PREFETCH_RADIUS) {
      prefetchedRef.current = true;
      // Same loader arguments AnimatedResident mounts with — a preload under
      // different options warms a different cache entry and buys nothing.
      for (const url of [npc.rigged, npc.walk, npc.idle].filter(Boolean)) {
        try { useGLTF.preload(url, true, true, ktx2); } catch { /* best effort */ }
      }
    }

    const want = bodyOnRef.current ? d < UNLOAD_RADIUS : d < LOAD_RADIUS;
    if (want) {
      if (bodyOnRef.current) return;
      // Re-register each frame while queued: it is idempotent (keyed by slug)
      // and keeps the distance fresh, so nearest-first stays true as you walk.
      queuedRef.current = true;
      requestLoad(npc.slug, d, () => { slotRef.current = true; bodyOnRef.current = true; queuedRef.current = false; setBodyOn(true); });
    } else if (bodyOnRef.current || queuedRef.current) {
      // Covers BOTH leaving after mounting and turning back while still in the
      // queue — an abandoned queue entry would otherwise mount a resident you
      // already walked away from, and a held slot would stall everyone behind it.
      releaseLoad(npc.slug, slotRef.current);
      bodyGone(npc.slug);
      slotRef.current = false; queuedRef.current = false;
      if (bodyOnRef.current) { bodyOnRef.current = false; setBodyOn(false); }
    }
  });
  // Leaving the street entirely must not strand a slot.
  useEffect(() => () => { releaseLoad(npc.slug, slotRef.current); bodyGone(npc.slug); slotRef.current = false; }, [npc.slug]);
  // The body is up — hand the slot back so the next resident can start.
  const onBodyReady = useCallback(() => {
    if (!slotRef.current) return;
    slotRef.current = false;
    releaseLoad(npc.slug, true);
  }, [npc.slug]);
  // In the placement editor you are moving residents from a distance, so they
  // all have to be visible regardless of where the camera is standing.
  const showBody = bodyOn || PLACE;

  const near = tier === 'near';
  // ONCE YOU ARE TALKING, THE INVITATION IS NOISE (Khari, 2026-08-04: "when we
  // are chatting, we are near the avatar.. we don't need to still see this button
  // on them, and it is close so it is large and blocking view"). The prompt
  // scales with proximity, and a conversation happens at the closest range there
  // is — so the thing telling you to start a chat grew to fill the screen over
  // the person you were already chatting to. It has done its job by then.
  const inChat = useWorldStore((st) => {
    const c = st.chatAvatar;
    if (!c) return false;
    return String(c.avatarId ?? c) === String(npc.avatarId);
  });
  const showLabel = PLACE || (tier !== 'far' && !inChat);
  const labelText = PLACE ? `${npc.name}${selected ? '  ◄ moving' : ''}`
    : (near && engageable ? `🎙 ${npc.name?.split(' ')[0] || 'talk'} — tap to talk` : npc.name);

  return (
    <>
      {!showBody && (
        // Placeholder while the body is unloaded: a capsule at the resident's
        // own height, so the street still reads as populated from across town.
        // Geometry only — no texture, no headshot — because the whole point is
        // that a distant resident costs nothing.
        <mesh position={[xf.pos[0], (xf.height || 1.8) * 0.5, xf.pos[2]]}>
          <capsuleGeometry args={[(xf.height || 1.8) * 0.13, (xf.height || 1.8) * 0.62, 4, 8]} />
          <meshStandardMaterial color="#2e2a3a" roughness={0.8} metalness={0} />
        </mesh>
      )}
      {showBody && (
      <AnimatedResident
        meshUrl={npc.rigged}
        rigPose={npc.pose || null}
        clipMap={npc.clips || null}
        clipUrls={[npc.walk, npc.idle].filter(Boolean)}
        play="idle"
        avatarId={npc.avatarId}
        position={xf.pos}
        rotationY={xf.rotY}
        height={xf.height}
        attentionRadius={PLACE ? null : (npc.attentionRadius ?? 2.4)}
        midRadius={npc.midRadius ?? 9}
        phaseOffset={phaseOffset}
        rate={rate}
        onTier={onTier}
        onReady={onBodyReady}
        onSelect={onSelect}
        onHover={onHover}
      />
      )}
      {showLabel && (
        <WorldLabel
          position={[xf.pos[0], (xf.height || 1.8) + 0.35, xf.pos[2]]}
          center distanceFactor={9} occlude={false}
          style={{ pointerEvents: 'none' }}
        >
          <div {...nameProps(onSelect, labelStyle(selected || (near && !PLACE)))}>{labelText}</div>
        </WorldLabel>
      )}
    </>
  );
}
